/*
 * This device (ToR 4.3.1, 4.3.2): an id the app makes once and keeps, registered with the server at
 * start, pinged at every start, sync and return online. The answer says whether the device is to be
 * wiped (then everything kept is purged, push unsubscribed, the person signed out), whether it is
 * blocked, and the offline policy. A device that cannot reach the server past its last sync plus the
 * policy's expiry purges itself as well.
 */
import { apiClient } from '@/api/apiClient';
import { useAuthStore } from '@/auth/authStore';
import { purgeStore } from './packStore';
import { forgetDataKey } from './sync';
import { unsubscribePush } from './push';

const ID_KEY = 'annova.device.id';
const NAME_KEY = 'annova.device.name';
const POLICY_KEY = 'annova.device.policy';
const LAST_CONTACT_KEY = 'annova.device.lastContact';

export interface OfflinePolicy { enabled: boolean; expiryDays: number; includePapers: boolean; maxDaysWithoutContact: number }
export interface DevicePing { wipe: boolean; blocked: boolean; offline: OfflinePolicy; pushConfigured: boolean }

function uuid(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const b = new Uint8Array(16); crypto.getRandomValues(b);
  b[6] = (b[6] & 0x0f) | 0x40; b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

export function deviceId(): string {
  try {
    let id = localStorage.getItem(ID_KEY);
    if (!id) { id = uuid(); localStorage.setItem(ID_KEY, id); }
    return id;
  } catch { return '00000000-0000-4000-8000-000000000000'; }
}

export function deviceName(): string {
  try { return localStorage.getItem(NAME_KEY) ?? `${platformName()} · ${browserName()}`; } catch { return 'This device'; }
}

export function setDeviceName(name: string) { try { localStorage.setItem(NAME_KEY, name); } catch { /* storage unavailable */ } }

export function platformName(): string {
  const ua = navigator.userAgent;
  if (/iPad|Macintosh.*Mobile/.test(ua)) return 'iPadOS';
  if (/iPhone/.test(ua)) return 'iOS';
  if (/Android/.test(ua)) return 'Android';
  if (/Windows/.test(ua)) return 'Windows';
  if (/Mac OS/.test(ua)) return 'macOS';
  if (/Linux/.test(ua)) return 'Linux';
  return 'Web';
}

function browserName(): string {
  const ua = navigator.userAgent;
  if (/Edg\//.test(ua)) return 'Edge';
  if (/Chrome\//.test(ua)) return 'Chrome';
  if (/Safari\//.test(ua) && !/Chrome/.test(ua)) return 'Safari';
  if (/Firefox\//.test(ua)) return 'Firefox';
  return 'Browser';
}

export function isInstalled(): boolean {
  return window.matchMedia?.('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export function cachedPolicy(): OfflinePolicy | null {
  try { const raw = localStorage.getItem(POLICY_KEY); return raw ? (JSON.parse(raw) as OfflinePolicy) : null; } catch { return null; }
}

export function lastContact(): Date | null {
  try { const raw = localStorage.getItem(LAST_CONTACT_KEY); return raw ? new Date(raw) : null; } catch { return null; }
}

function remember(ping: DevicePing) {
  try {
    localStorage.setItem(POLICY_KEY, JSON.stringify(ping.offline));
    localStorage.setItem(LAST_CONTACT_KEY, new Date().toISOString());
  } catch { /* storage unavailable */ }
}

/** Purges everything the app kept on this device, tells the service worker, unsubscribes push, and (when asked) signs the person out. */
export async function wipeThisDevice(reason: 'server' | 'expired' | 'signout', signOut = true): Promise<void> {
  try { await unsubscribePush(); } catch { /* best effort */ }
  forgetDataKey();
  await purgeStore();
  try { navigator.serviceWorker?.controller?.postMessage({ type: 'WIPE' }); } catch { /* no worker */ }
  try { localStorage.removeItem(POLICY_KEY); localStorage.removeItem(LAST_CONTACT_KEY); localStorage.removeItem('annova.lock.pin'); } catch { /* storage unavailable */ }
  if (reason === 'server') {
    try { await apiClient.post(`/api/v1/devices/${deviceId()}/wiped`); } catch { /* reported at the next contact */ }
  }
  if (signOut) {
    useAuthStore.getState().clear();
    window.location.assign(`/login?reason=${reason === 'server' ? 'wiped' : reason === 'expired' ? 'expired' : 'signout'}`);
  }
}

/** Registers at start (and on every sign-in); carries out a wipe the server asked for. */
export async function registerThisDevice(): Promise<DevicePing | null> {
  if (!useAuthStore.getState().isAuthenticated) return null;
  try {
    const { data } = await apiClient.post<DevicePing>('/api/v1/devices/register', {
      deviceId: deviceId(), name: deviceName(), platform: platformName(), userAgent: navigator.userAgent.slice(0, 500), appVersion: import.meta.env.VITE_APP_VERSION ?? null,
    });
    remember(data);
    if (data.wipe || data.blocked) { await wipeThisDevice('server'); return data; }
    return data;
  } catch { return null; }
}

/** Every return online and every sync: the server's answer wins over whatever the device believes. */
export async function pingThisDevice(synced = false): Promise<DevicePing | null> {
  if (!useAuthStore.getState().isAuthenticated) return null;
  try {
    const { data } = await apiClient.post<DevicePing>(`/api/v1/devices/${deviceId()}/ping`, { synced });
    remember(data);
    if (data.wipe || data.blocked) { await wipeThisDevice('server'); }
    return data;
  } catch (err) {
    const status = (err as { response?: { status?: number } }).response?.status;
    // An unknown device (a fresh server, a deleted row) registers again; anything else is a network matter.
    if (status === 404) return registerThisDevice();
    return null;
  }
}

/** Offline and silent past the policy's limit: the device purges itself without being told (ToR 4.3.1). */
export async function purgeIfSilentTooLong(): Promise<boolean> {
  const policy = cachedPolicy(); const last = lastContact();
  if (!policy || !last) return false;
  const days = (Date.now() - last.getTime()) / 86_400_000;
  if (days > policy.maxDaysWithoutContact) { await wipeThisDevice('expired'); return true; }
  return false;
}
